import React, { useEffect } from 'react';
import '../giftcard-shop/GiftCardShop.css';
import { CART_KEY } from './useCart';

// Standalone landing page after the Stripe product checkout redirect
// (/store/order/:orderId?status=success|cancel&salon=…&sn=…&ref=…&m=…).
// Shares the gift-card confirmation's design system (gcs-confirm) so every
// post-payment screen reads as one family. The query context is display-only —
// set by our backend on the Stripe success/cancel URLs, never trusted for money.

const isMongoId = (s) => /^[a-f0-9]{24}$/i.test(s || '');

const SuccessIcon = () => (
  <svg width="56" height="56" viewBox="0 0 48 48" fill="none" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="24" cy="24" r="21" stroke="#d6f0e0" strokeWidth="3" />
    <path d="M15 24.5l6 6 12-13" stroke="#1a8a4a" strokeWidth="3" />
  </svg>
);
const CancelIcon = () => (
  <svg width="56" height="56" viewBox="0 0 48 48" fill="none" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="24" cy="24" r="21" stroke="#f4d7d4" strokeWidth="3" />
    <path d="M17 17l14 14M31 17l-14 14" stroke="#b42318" strokeWidth="3" />
  </svg>
);

const ProductOrderSuccess = ({ match, location }) => {
  const params = new URLSearchParams(location && location.search);
  const cancelled = params.get('status') === 'cancel';
  const salonParam = params.get('salon') || '';
  const salonName = params.get('sn') || '';
  const method = params.get('m'); // 'ship' | 'collect'
  const orderId = match && match.params && match.params.orderId;
  const orderRef = params.get('ref') || (orderId ? String(orderId).slice(-8).toUpperCase() : '');

  // Paid: the basket is spent. Cancelled keeps it ("Your basket is saved").
  useEffect(() => {
    if (!cancelled) { try { localStorage.removeItem(CART_KEY); } catch (e) { /* ignore */ } }
  }, [cancelled]);

  const salonBase = salonParam ? (isMongoId(salonParam) ? `/salon/${salonParam}` : `/book/${salonParam}`) : '';
  const shopHref = salonBase ? `${salonBase}/shop` : '/';
  const salonHref = salonBase || '/';

  const sub = cancelled
    ? "No payment was taken and your order wasn't placed. Your basket is saved — you can pick up where you left off whenever you're ready."
    : method === 'ship'
      ? `Thanks — your order is in and paid. We've emailed your confirmation, and ${salonName || 'the salon'} will let you know when it's been shipped.`
      : `Thanks — your order is in and paid. We've emailed your confirmation, and ${salonName || 'the salon'} will let you know when it's ready to collect.`;

  return (
    <div className="gcs">
      <header className="gcs-header">
        <div className="gcs-header-title">
          {salonName && <span className="gcs-header-eyebrow">{salonName}</span>}
          <h1 className="gcs-header-h1">Shop</h1>
        </div>
      </header>

      <div className="gcs-confirm">
        <div className="gcs-confirm-icon" aria-hidden>
          {cancelled ? <CancelIcon /> : <SuccessIcon />}
        </div>
        <h2 className="gcs-confirm-title">{cancelled ? 'Payment cancelled' : 'Order confirmed'}</h2>
        <p className="gcs-confirm-sub">{sub}</p>

        {!cancelled && orderRef && (
          <div className="gcs-confirm-code">
            <span className="gcs-confirm-code-label">Order reference</span>
            <span className="gcs-confirm-code-value">{orderRef}</span>
          </div>
        )}

        <div className="gcs-confirm-actions">
          <a href={shopHref} className={cancelled ? 'gcs-btn' : 'gcs-btn-ghost'} style={{ textDecoration: 'none' }}>
            {cancelled ? 'Back to the shop' : 'Continue shopping'}
          </a>
          <a href={salonHref} className={cancelled ? 'gcs-btn-ghost' : 'gcs-btn'} style={{ textDecoration: 'none' }}>
            Back to {salonName || 'the salon'}
          </a>
        </div>
      </div>
    </div>
  );
};

export default ProductOrderSuccess;
